import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FilterBar, type FilterFieldDef } from '@components/ui/FilterMenu';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { createDefaultClaimWorkPredicates } from '../../billing/components/v2/claims/claimWorkFilters';
import {
  applyClaimWorkUrlState,
  type ClaimWorkUrlState,
  parseClaimWorkUrlState,
  railViewKey,
} from '../../billing/components/v2/claims/claimWorkUrlState';
import { claimWorkViewLabelForCount } from '../../billing/components/v2/claims/claimWorkViewGroups';
import { ClaimWorkViewRail } from '../../billing/components/v2/claims/ClaimWorkViewRail';
import { ShowHiddenRowsToggle } from '../../billing/components/v2/claims/ShowHiddenRowsToggle';
import { DispositionChip } from '../../billing/components/v2/shared/AttentionIndicator';
import { type ColumnDef, DataTable } from '../../billing/components/v2/shared/DataTable';
import { formatCents } from '../../billing/components/v2/shared/formatCents';
import { isQueueViewKey, WORK_VIEW_LABELS } from '../../billing/types/encounterWork';
import { formatDateOnly } from '../../billing/utils';
import {
  workStateChipColor,
  workStateChipLabel,
  workStateChipVariant,
} from '../../billing/utils/claimWorkPresentation';
import { useSubjectLanguageVariants } from '../../user/store/hooks';
import { AdminClaimPatientFilter } from '../components/admin-claim-patient-filter';
import {
  useAdminClaimFilterOptionsQuery,
  useAdminClaimPatientOptionsQuery,
  useAdminClaimWorkQuery,
} from '../store/hooks';
import type { AdminClaimWorkItem } from '../types';
import { reportedCount } from '../utils/reported-count';

import { adminClaimWorkQueryArgs } from './admin-claim-work-query';

const columnsForSubject = (subjectLabel: string): ColumnDef[] => [
  { key: 'claim_number', label: 'Claim', width: '1fr' },
  { key: 'service_date', label: 'Service date', width: '110px' },
  { key: 'patient_name', label: subjectLabel, width: '1.2fr' },
  { key: 'payer_name', label: 'Payer', width: '1.1fr' },
  { key: 'clinician_display_name', label: 'Provider', width: '1.1fr' },
  { key: 'work_state', label: 'Status', width: '150px' },
  { key: 'disposition', label: 'Work status', width: '120px' },
  { key: 'total_charge_cents', label: 'Charge', width: '90px', align: 'right' },
];

const NO_ACTION = () => {};

/** The admin's read-only lens over the same rows, filters and counts as Claims. */
export default function AdminClaimWorklist({ organizationId }: { organizationId: string }) {
  const { singularCap, plural } = useSubjectLanguageVariants();
  const [searchParams, setSearchParams] = useSearchParams();
  const state = useMemo(() => parseClaimWorkUrlState(searchParams), [searchParams]);
  const scope = `${organizationId}:${searchParams.toString()}`;
  const [paging, setPaging] = useState<{ scope: string; cursors: Array<string | undefined> }>({
    scope,
    cursors: [undefined],
  });
  const cursors = paging.scope === scope ? paging.cursors : [undefined];
  const args = adminClaimWorkQueryArgs(organizationId, state, cursors.at(-1));
  const queryOptions = {
    refetchOnFocus: true,
    refetchOnMountOrArgChange: 15,
    refetchOnReconnect: true,
  };
  const {
    currentData: filterOptions,
    error: filterOptionsError,
    isLoading: filterOptionsLoading,
    refetch: refetchFilterOptions,
  } = useAdminClaimFilterOptionsQuery({ organizationId }, queryOptions);
  const patientPredicate = state.predicates.patient;
  const selectedPatientIds =
    patientPredicate?.kind === 'multiSelect' ? patientPredicate.values : [];
  const {
    currentData: selectedPatients,
    error: selectedPatientError,
    refetch: refetchSelectedPatient,
  } = useAdminClaimPatientOptionsQuery(
    { organizationId, patientIds: selectedPatientIds[0], limit: 1 },
    { ...queryOptions, skip: selectedPatientIds.length !== 1 },
  );
  const { currentData, error, isFetching, isLoading, refetch } = useAdminClaimWorkQuery(
    args,
    queryOptions,
  );
  const {
    currentData: visibleCounts,
    error: visibleCountsError,
    refetch: refetchVisibleCounts,
  } = useAdminClaimWorkQuery(
    { ...args, cursor: undefined, includeHidden: false, limit: 1 },
    { ...queryOptions, skip: !args.includeHidden },
  );
  const rows = currentData?.items ?? [];
  const counts = (args.includeHidden ? visibleCounts : currentData)?.counts;
  const activeView = railViewKey(state.workView);
  const isQueue = isQueueViewKey(activeView);
  const update = (change: Partial<ClaimWorkUrlState>) =>
    setSearchParams(
      previous =>
        applyClaimWorkUrlState(previous, { ...parseClaimWorkUrlState(previous), ...change }),
      { replace: true },
    );
  const fields: FilterFieldDef[] = [
    { id: 'serviceDate', label: 'Service date', type: 'dateRange' },
    {
      id: 'provider',
      label: 'Provider',
      type: 'multiSelect',
      options: (filterOptions?.providers ?? []).map(provider => ({
        value: provider.user_id,
        label: provider.name,
      })),
      loading: filterOptionsLoading,
      searchPlaceholder: 'Search providers',
    },
    {
      id: 'payer',
      label: 'Payer',
      type: 'multiSelect',
      options: (filterOptions?.payers ?? []).map(payer => ({
        value: payer.payer_uuid,
        label: payer.payer_name,
      })),
      loading: filterOptionsLoading,
      searchPlaceholder: 'Search payers',
    },
    {
      id: 'patient',
      label: singularCap,
      type: 'custom',
      describeValue: value => {
        const ids = value.kind === 'multiSelect' ? value.values : [];
        return {
          op: 'is',
          value:
            ids.length > 1
              ? `${ids.length} ${plural}`
              : (selectedPatients?.patients?.find(patient => patient.patient_id === ids[0])?.name ??
                singularCap),
        };
      },
      renderEditor: props => (
        <AdminClaimPatientFilter
          key={organizationId}
          {...props}
          organizationId={organizationId}
          searchLabel={`Search ${plural}`}
        />
      ),
    },
    {
      id: 'claimLifecycle',
      label: 'Claim status',
      type: 'multiSelect',
      options: [
        'draft',
        'processing',
        'ready',
        'submitted',
        'acknowledged',
        'accepted',
        'rejected',
        'denied',
        'paid',
        'partially_paid',
        'reversed',
      ].map(value => ({ value, label: value.replaceAll('_', ' ') })),
    },
  ];
  const dateRange = state.predicates.serviceDate;
  const dateScope =
    dateRange?.kind === 'dateRange'
      ? dateRange.windowDays
        ? `Last ${dateRange.windowDays} days`
        : `${dateRange.start ?? 'Any date'} – ${dateRange.end ?? 'today'}`
      : 'All time';
  const matchingCount = currentData?.counts.matching_count ?? 0;
  /* A count the server did not send stays null, and the statement below
     leaves it out rather than printing 0. `bill_secondary` reaches the
     generated counts with smartscribe-server GDN-4022 (PR preflight). */
  const queueCount = reportedCount(counts?.work_view_counts?.[activeView]);
  const hiddenShown = rows.filter(row => row.hidden).length;
  const queueLabel = claimWorkViewLabelForCount(activeView, queueCount ?? 0);
  const statement = !currentData
    ? `${error ? 'Claims unavailable' : 'Loading claims'} · ${dateScope}`
    : args.includeHidden
      ? `${counts && queueCount != null ? `${queueCount} ${queueLabel.charAt(0).toLowerCase() + queueLabel.slice(1)} · ` : ''}${hiddenShown} hidden row${hiddenShown === 1 ? '' : 's'} shown · ${dateScope}`
      : `${matchingCount} matching claim${matchingCount === 1 ? '' : 's'} · ${dateScope}`;

  return (
    <Box component="section" aria-label="Organization claims" sx={{ mt: 3 }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
        Claims
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '240px minmax(0, 1fr)' },
          gap: 2,
          alignItems: 'start',
        }}
      >
        <ClaimWorkViewRail
          activeSavedViewId={null}
          activeWorkView={activeView}
          counts={counts?.work_view_counts ?? {}}
          countsPending={!counts}
          dirty={false}
          isMutating={false}
          savedViews={[]}
          showSavedViews={false}
          onSelectWorkView={workView => update({ workView, showHidden: false, savedViewId: null })}
          onDelete={NO_ACTION}
          onSave={NO_ACTION}
          onSelectSavedView={NO_ACTION}
          onUpdate={NO_ACTION}
        />
        <Stack spacing={2} sx={{ minWidth: 0 }}>
          <Typography variant="h6">{WORK_VIEW_LABELS[activeView]}</Typography>
          {filterOptionsError || selectedPatientError ? (
            <Alert
              severity="error"
              action={
                <Button
                  onClick={() => {
                    if (filterOptionsError) void refetchFilterOptions();
                    if (selectedPatientError) void refetchSelectedPatient();
                  }}
                >
                  Retry filters
                </Button>
              }
            >
              Filter options could not be loaded.
            </Alert>
          ) : null}
          <FilterBar
            canClearPredicates={Object.keys(state.predicates).length > 0}
            canResetView
            fields={fields}
            predicates={state.predicates}
            resultStatement={statement}
            onPredicatesChange={predicates => update({ predicates })}
            onClearPredicates={() => update({ predicates: {} })}
            onResetView={() =>
              update({ predicates: createDefaultClaimWorkPredicates(), showHidden: false })
            }
            utilityControl={
              isQueue ? (
                <ShowHiddenRowsToggle
                  on={state.showHidden}
                  onChange={showHidden => update({ showHidden })}
                />
              ) : undefined
            }
          />
          <Stack direction="row" spacing={1}>
            <TextField
              select
              size="small"
              label="Sort"
              value={state.sort}
              onChange={event => update({ sort: event.target.value as ClaimWorkUrlState['sort'] })}
            >
              <MenuItem value="service_date_desc">Service date · newest</MenuItem>
              <MenuItem value="service_date_asc">Service date · oldest</MenuItem>
              <MenuItem value="charge_desc">Charge · highest</MenuItem>
              <MenuItem value="charge_asc">Charge · lowest</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              label="Rows"
              value={state.perPage}
              onChange={event => update({ perPage: Number(event.target.value) })}
            >
              {[25, 50, 100].map(value => (
                <MenuItem key={value} value={value}>
                  {value}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          {error ? (
            <Alert severity="error" action={<Button onClick={refetch}>Retry</Button>}>
              Claims could not be loaded. This view requires access to the organization’s claim
              details.
            </Alert>
          ) : null}
          {args.includeHidden && visibleCountsError ? (
            <Alert
              severity="error"
              action={
                <Button
                  onClick={() => {
                    void refetch();
                    void refetchVisibleCounts();
                  }}
                >
                  Retry counts
                </Button>
              }
            >
              Active queue counts could not be refreshed. Hidden claims are still shown below.
            </Alert>
          ) : null}
          {isLoading ? <CircularProgress size={24} /> : null}
          {!error && currentData && rows.length === 0 ? (
            <Alert severity="info">No claims match these filters.</Alert>
          ) : null}
          <DataTable
            columns={columnsForSubject(singularCap)}
            idKey="claim_id"
            rows={rows}
            isFetching={isFetching}
            renderCell={(column, tableRow) => {
              const row = tableRow as AdminClaimWorkItem;
              if (column.key === 'work_state')
                return (
                  <Chip
                    size="small"
                    label={workStateChipLabel(row)}
                    color={workStateChipColor(row)}
                    variant={workStateChipVariant(row)}
                  />
                );
              if (column.key === 'total_charge_cents')
                return <>{formatCents(row.total_charge_cents ?? 0)}</>;
              if (column.key === 'service_date') return <>{formatDateOnly(row.service_date)}</>;
              if (column.key === 'disposition')
                return (
                  <DispositionChip
                    disposition={
                      row.work_disposition
                        ? {
                            ...row.work_disposition,
                            hidden_until: row.work_disposition.hidden_until ?? null,
                            actor_principal_id: row.work_disposition.actor_principal_id ?? null,
                            actor_principal_kind: row.work_disposition.actor_principal_kind ?? null,
                            actor_name: row.work_disposition.actor_name ?? null,
                            updated_at: row.work_disposition.updated_at ?? '',
                          }
                        : null
                    }
                    returnedReason={row.returned_reason}
                    lastPayerStatus={
                      row.last_payer_status
                        ? {
                            ...row.last_payer_status,
                            code: row.last_payer_status.code ?? null,
                            occurred_at: row.last_payer_status.occurred_at ?? '',
                          }
                        : null
                    }
                  />
                );
              return null;
            }}
          />
          <Stack
            direction="row"
            spacing={1}
            sx={{ alignItems: 'center', justifyContent: 'flex-end' }}
          >
            <Typography variant="caption">Page {cursors.length}</Typography>
            <Button
              disabled={cursors.length < 2 || isFetching}
              onClick={() => setPaging({ scope, cursors: cursors.slice(0, -1) })}
            >
              Previous
            </Button>
            <Button
              disabled={!currentData?.next_cursor || isFetching}
              onClick={() =>
                setPaging({ scope, cursors: [...cursors, currentData?.next_cursor ?? undefined] })
              }
            >
              Next
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
}
